import React, {useState, useEffect, useRef, useCallback} from "react";
import {useParams, Link} from "react-router-dom";
import "./LiveRoomPage.css";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const JAAS_APP_ID = process.env.REACT_APP_JAAS_APP_ID || "";
const JITSI_HOST = "meet.jit.si";

function JitsiCall({roomName, displayName, onEnd}) {
    const containerRef = useRef(null);
    const apiRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            if (!containerRef.current) return;

            let jwt = null;
            if (JAAS_APP_ID) {
                try {
                    const r = await fetch(`${API_BASE}/api/live-tour/jaas-token`, {
                        method: "POST",
                        headers: {"Content-Type": "application/json"},
                        body: JSON.stringify({userName: displayName, roomName, moderator: "false"}),
                    });
                    if (r.ok) jwt = (await r.json()).token;
                } catch { /* non-critical */
                }
            }

            const domain = JAAS_APP_ID ? "8x8.vc" : JITSI_HOST;
            const jaasRoom = JAAS_APP_ID ? `${JAAS_APP_ID}/${roomName}` : roomName;

            const initApi = () => {
                if (!containerRef.current || !window.JitsiMeetExternalAPI) return;
                const opts = {
                    roomName: jaasRoom,
                    parentNode: containerRef.current,
                    width: "100%",
                    height: "100%",
                    userInfo: {displayName: displayName || "Customer"},
                    configOverwrite: {
                        startWithAudioMuted: false,
                        startWithVideoMuted: false,
                        prejoinPageEnabled: false,
                        prejoinConfig: {enabled: false},
                        disableDeepLinking: true,
                        p2p: {enabled: false},
                    },
                    interfaceConfigOverwrite: {
                        SHOW_JITSI_WATERMARK: false,
                        SHOW_BRAND_WATERMARK: false,
                        SHOW_POWERED_BY: false,
                        DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                        TOOLBAR_ALWAYS_VISIBLE: true,
                        MOBILE_APP_PROMO: false,
                    },
                };
                if (jwt) opts.jwt = jwt;
                apiRef.current = new window.JitsiMeetExternalAPI(domain, opts);
                apiRef.current.addEventListeners({
                    readyToClose: () => onEnd?.(),
                    participantLeft: () => {
                        const count = apiRef.current?.getNumberOfParticipants?.() || 0;
                        if (count <= 1) setTimeout(() => onEnd?.(), 4000);
                    },
                });
            };

            const scriptSrc = JAAS_APP_ID
                ? `https://8x8.vc/${JAAS_APP_ID}/external_api.js`
                : `https://${JITSI_HOST}/external_api.js`;

            if (window.JitsiMeetExternalAPI) {
                initApi();
            } else {
                const s = document.createElement("script");
                s.src = scriptSrc;
                s.async = true;
                s.onload = initApi;
                document.body.appendChild(s);
            }
        };

        load();
        return () => {
            apiRef.current?.dispose();
        };
    }, [roomName, displayName]); // eslint-disable-line

    return <div ref={containerRef} style={{width: "100%", height: "100%"}}/>;
}

export default function LiveRoomPage() {
    const {roomName} = useParams();

    const [step, setStep] = useState("enter"); // enter | call | ended
    const [nameInput, setNameInput] = useState("");

    const handleJoin = useCallback(() => {
        if (!nameInput.trim()) return;
        setStep("call");
    }, [nameInput]);

    // ── Active call ──────────────────────────────────────────────────────────
    if (step === "call") {
        return (
            <div className="lrp-call-screen">
                <div className="lrp-call-bar">
                    <div className="lrp-brand">
                        <div className="lrp-brand-icon">OGM</div>
                        <div>
                            <span className="lrp-brand-name">OGM Live</span>
                            <span className="lrp-brand-sub">Live Property Tour</span>
                        </div>
                    </div>
                    <div className="lrp-live-pill">
                        <span className="lrp-live-dot"/>
                        Live
                    </div>
                    <button className="lrp-end-btn" onClick={() => setStep("ended")}>
                        End Tour
                    </button>
                </div>
                <div className="lrp-jitsi-wrap">
                    <JitsiCall
                        roomName={roomName}
                        displayName={nameInput}
                        onEnd={() => setStep("ended")}
                    />
                </div>
            </div>
        );
    }

    // ── Ended ────────────────────────────────────────────────────────────────
    if (step === "ended") {
        return (
            <div className="lrp-page lrp-page--center">
                <div className="lrp-card lrp-thankyou">
                    <div className="lrp-ty-icon">🏡</div>
                    <h2 className="lrp-ty-title">Thanks for joining!</h2>
                    <p className="lrp-ty-sub">
                        We hope you enjoyed the live property tour.
                        Our advisor will follow up with you shortly.
                    </p>
                    <Link to="/" className="lrp-btn lrp-btn--primary">
                        Browse Properties →
                    </Link>
                </div>
            </div>
        );
    }

    // ── Entry screen ─────────────────────────────────────────────────────────
    return (
        <div className="lrp-page lrp-page--center">
            <div className="lrp-card lrp-entry">

                {/* Header */}
                <div className="lrp-entry-header">
                    <div className="lrp-brand-icon lrp-brand-icon--lg">OGM</div>
                    <div className="lrp-entry-live">
                        <span className="lrp-live-dot"/>
                        <span>Live Now</span>
                    </div>
                </div>

                <h1 className="lrp-entry-title">Join Live Property Tour</h1>
                <p className="lrp-entry-sub">
                    You've been invited to a live property walkthrough with an OGM advisor.
                    Enter your name to join.
                </p>

                <div className="lrp-entry-form">
                    <input
                        type="text"
                        className="lrp-input"
                        placeholder="Your name"
                        value={nameInput}
                        onChange={e => setNameInput(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleJoin()}
                        autoFocus
                        autoComplete="name"
                    />
                    <button
                        className="lrp-btn lrp-btn--join"
                        onClick={handleJoin}
                        disabled={!nameInput.trim()}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                             strokeLinejoin="round">
                            <polygon points="23 7 16 12 23 17 23 7"/>
                            <rect x="1" y="5" width="15" height="14" rx="2"/>
                        </svg>
                        Join Live Tour
                    </button>
                </div>

                <div className="lrp-features">
                    {[
                        ["🎥", "HD video walkthrough"],
                        ["💬", "Ask questions live"],
                        ["🔒", "No account needed"],
                    ].map(([icon, text]) => (
                        <div key={text} className="lrp-feature">
                            <span className="lrp-feature-icon">{icon}</span>
                            <span>{text}</span>
                        </div>
                    ))}
                </div>

                <p className="lrp-footer-note">
                    <Link to="/" className="lrp-link">One Global Marketplace</Link>
                    {" "} · Premium real estate platform
                </p>
            </div>
        </div>
    );
}